// Additional Help Center articles so EVERY area of the app has documentation.
// Same shape as the SECTIONS array in HelpPage.js: { id, title, icon, content }.
// These are concatenated into SECTIONS and placed into the Help menu via
// config/helpNav.js (which mirrors the main application navigation).
import {
  Search, FileText, Briefcase, Target, Flag, CheckCircle2, Clock, Scale,
  ShieldCheck, TrendingUp, BadgeCheck, Ruler, Package, Sparkles, Network,
  GitPullRequest, Bot, Mic, Activity, DollarSign, Paintbrush, GitBranch,
  MessageSquare, BarChart3, Database, Users, KeyRound, ScrollText, Server,
  RefreshCw, Globe, HardDrive, Settings, Lightbulb, Code, Bell, Building2,
} from 'lucide-react';

export const HELP_SECTIONS_EXTRA = [
  // ── ANALYZE ──────────────────────────────────────────────────────────
  {
    id: 'investigations', title: 'Investigations', icon: Search,
    content: `
## Investigations
Investigations let you drill from a surprising number to its root cause. Start one from any Finding, KPI tile, or Explore answer via **Investigate**.

### What it does
- Runs a governed driver analysis (period-over-period and segment contribution) on the exact measure you're looking at
- Breaks a change down by dimension so you can see which segments moved the metric
- Captures each step so the reasoning is auditable and shareable

### Tips
- Open **Investigations → Diff** to compare two runs side by side
- Use **Start a thread** to turn an investigation into a discussion with teammates
`,
  },
  {
    id: 'executive-summaries', title: 'Executive Briefings', icon: FileText,
    content: `
## Executive Briefings
AI-written narratives that summarise the state of a model or dashboard for leadership — headline movements, risks, and recommended actions in plain language.

### Working with briefings
1. Pick the model/dashboard and the period
2. The AI drafts a briefing with KPIs, trends, and callouts
3. Edit any section inline, then share or schedule it for recurring delivery

**Tip:** Briefings honour your governance scope — only certified measures and data you're allowed to see are included.
`,
  },

  // ── DECISIONS ────────────────────────────────────────────────────────
  {
    id: 'my-work', title: 'My Work', icon: Briefcase,
    content: `
## My Work
Your personal queue across the platform: assigned findings, open discussions, change requests awaiting you, recommendations, and tasks from initiatives.

### Columns
- **Needs you** — items where you're the owner or approver
- **Following** — threads and items you're watching
- **Recommendations** — AI-suggested next actions you can accept or dismiss

**Tip:** Clearing an item here also updates the source (discussion, initiative, or change request).
`,
  },
  {
    id: 'initiatives', title: 'Initiatives', icon: Flag,
    content: `
## Initiatives
Initiatives group the work behind a business outcome — the goals, the metrics that measure them, and the tasks and discussions driving progress.

### Structure
- **Goal** the initiative is trying to move
- **Linked metrics** that track it
- **Tasks & owners** with due dates
- **Activity** showing recent progress

**Tip:** Link an initiative to a KPI so its tile shows live progress toward target.
`,
  },
  {
    id: 'objectives', title: 'Goals & Guardrails', icon: Target,
    content: `
## Goals & Guardrails
Define measurable **goals** (targets you want to improve) and **guardrails** (thresholds you must not breach) on any certified metric.

### How it works
1. Choose a metric and set a target value and timeframe for a goal, or a min/max band for a guardrail
2. The platform tracks the metric against the goal/guardrail automatically
3. Breaches surface as Findings and can trigger Alerts

**Tip:** Guardrails pair well with Sentinels for automated monitoring.
`,
  },
  {
    id: 'results', title: 'Results', icon: CheckCircle2,
    content: `
## Results
A scoreboard of how your goals and initiatives are tracking — actuals vs target, trend, and status (on track / at risk / off track).

### Reading the board
- **On track** (green) — trending to hit target
- **At risk** (amber) — slipping; open the item to investigate
- **Off track** (red) — breach or missed target

**Tip:** Click any row to jump to the initiative or goal and its underlying metric.
`,
  },
  {
    id: 'retention', title: 'Retention', icon: Clock,
    content: `
## Retention
Preview and manage how long reports, dashboards, and data are kept before archival, per your workspace retention policy.

### What you can do
- See which assets are approaching their retention cut-off
- Extend or exempt specific assets
- Understand the archive schedule before anything is removed

**Tip:** Retention never deletes silently — expiring items are listed here first.
`,
  },
  {
    id: 'decision-policies', title: 'Decision Policies', icon: Scale,
    content: `
## Decision Policies
Codify the rules that govern automated and AI-assisted decisions — who can approve what, when human sign-off is required, and which actions the AI Coach may take autonomously.

### Policy parts
- **Scope** — the metrics, tenants, or actions a policy covers
- **Conditions** — thresholds that trigger the policy
- **Required approvals** — the roles that must sign off

**Tip:** Policies are enforced everywhere decisions are made, including recommendations and autonomous actions.
`,
  },

  // ── GOVERNANCE ───────────────────────────────────────────────────────
  {
    id: 'governance', title: 'Governance Overview', icon: ShieldCheck,
    content: `
## Governance Overview
The control center for trust in your analytics: certification status, data quality, lineage coverage, PII exposure, and adoption — all on one page.

### Panels
- **Certification** — how many measures are certified vs draft
- **Data Quality** — open quality issues by severity
- **Coverage** — lineage and AI-readiness scores
- **Access** — recent permission and PII-policy changes

**Tip:** Each panel links to its dedicated tool for deeper work.
`,
  },
  {
    id: 'adoption-analytics', title: 'Adoption Analytics', icon: TrendingUp,
    content: `
## Adoption Analytics
See how your organisation actually uses the platform — active users, most-opened dashboards, Explore queries, and features gaining or losing traction.

### Uses
- Spot dashboards nobody opens (candidates to retire)
- Find power users and champions
- Measure the impact of a rollout or training

**Tip:** Filter by department or role to compare adoption across teams.
`,
  },
  {
    id: 'certification-queue', title: 'Certification Queue', icon: BadgeCheck,
    content: `
## Certification Queue
Where data stewards review and certify measures and dimensions so consumers know what's trusted.

### Workflow
1. An author submits a measure for certification
2. A steward reviews the formula, lineage, and description
3. **Certify** (adds the trusted badge) or **send back** with comments

**Tip:** Only certified measures appear in governed features like Morning Brief and Executive Briefings.
`,
  },
  {
    id: 'metric-registry', title: 'Metric Registry', icon: Ruler,
    content: `
## Metric Registry
The single catalogue of every business metric — its definition, owner, formula, certification status, and where it's used.

### Why it matters
- One agreed definition per metric (no more "which revenue?")
- Owners and descriptions for accountability
- Usage links show every report/dashboard that references a metric

**Tip:** Search the registry before building a new measure — it may already exist.
`,
  },
  {
    id: 'calculation-packs', title: 'Calculation Packs', icon: Package,
    content: `
## Calculation Packs
Reusable bundles of calculated measures and dimensions (e.g. an insurance loss-ratio pack) you can apply to a model in one step.

### Working with packs
- Browse available packs and preview the formulas they add
- Apply a pack to a compatible model to add all its measures at once
- Packs are versioned so updates can be reviewed before adoption

**Tip:** Build your own pack from measures you reuse across models.
`,
  },
  {
    id: 'data-quality', title: 'Data Quality', icon: ShieldCheck,
    content: `
## Data Quality
Automated checks on your models — nulls, type mismatches, out-of-range values, freshness, and referential gaps — surfaced as ranked issues.

### Resolving issues
1. Review the issue, its severity, and the affected column/rows
2. Fix at source or in Data Prep, or add a rule exception
3. Re-run the check to confirm it's cleared

**Tip:** Quality issues can be promoted to Findings so they're tracked to resolution.
`,
  },
  {
    id: 'data-catalog', title: 'Data Catalog', icon: Database,
    content: `
## Data Catalog
A searchable inventory of your data assets — sources, tables, columns, models, and their descriptions, tags, and owners.

### Uses
- Discover what data exists and who owns it
- See column-level descriptions and sensitivity tags
- Jump from a catalog entry to its lineage or model

**Tip:** Good tags and descriptions here power better AI Explore answers.
`,
  },
  {
    id: 'ai-coverage', title: 'AI Readiness', icon: Sparkles,
    content: `
## AI Readiness
Scores how prepared each model is for reliable AI Explore — are measures described, synonyms added, relationships defined, and types clean?

### Improving the score
- Add business descriptions and synonyms to fields
- Certify key measures
- Resolve data-quality and relationship gaps

**Tip:** Higher readiness = more accurate natural-language answers and fewer clarifying questions.
`,
  },
  {
    id: 'explore-eval', title: 'AI Quality', icon: BadgeCheck,
    content: `
## AI Quality
Evaluate and track how well AI Explore answers your questions — accuracy, grounding, and regressions over time using test question sets.

### What you do here
- Run evaluation suites against a model
- Review graded answers and failures
- Track quality trends as you tune the model

**Tip:** Add real user questions that went wrong as new test cases.
`,
  },
  {
    id: 'pii-compliance', title: 'PII & Data Access', icon: ShieldCheck,
    content: `
## PII & Data Access
Classify sensitive columns and control who can see them with row- and column-level policies.

### Controls
- **PII tagging** — mark columns as sensitive/restricted
- **Masking** — hide or partially mask values for unauthorised roles
- **Row-level security** — limit rows a role/user can see

**Tip:** PII policies apply everywhere — reports, Explore, and exports — so data can't leak through a side door.
`,
  },
  {
    id: 'governance-analytics', title: 'Governance Analytics', icon: BarChart3,
    content: `
## Governance Analytics
Metrics about your governance program itself — certification rates, time-to-certify, open quality issues, and policy coverage over time.

**Tip:** Use these trends to report governance maturity to leadership and spot bottlenecks in the certification workflow.
`,
  },
  {
    id: 'change-requests', title: 'Change Requests', icon: GitPullRequest,
    content: `
## Change Requests
Proposed changes to governed assets (models, measures, dashboards) that need review before they go live — your analytics change-control workflow.

### Lifecycle
1. An author opens a change request describing the change
2. Reviewers compare before/after and comment
3. **Approve** promotes the change; **reject** sends it back

**Tip:** Change requests pair with Version Control so every approved change is tracked and reversible.
`,
  },
  {
    id: 'coach-studio', title: 'Coach Studio', icon: Sparkles,
    content: `
## Coach Studio
Author and manage what the AI Coach can teach and do — coachable tasks, guided walkthroughs, and the controls the Coach is allowed to operate.

### In the studio
- Record or define a coachable task
- Map UI controls the Coach can reference or drive
- Set autonomy limits and review coverage gaps

**Tip:** See **AI Coach** and **Coach Autonomy** help for how tasks are delivered to users.
`,
  },

  // ── DEVELOPER ────────────────────────────────────────────────────────
  {
    id: 'sdk-visuals', title: 'Custom Visuals (SDK)', icon: Code,
    content: `
## Custom Visuals
Build and register your own chart types using the Visual SDK, then use them anywhere alongside built-in visuals.

### Flow
1. Scaffold a visual with the SDK and implement its render + config schema
2. Upload/register it in this console
3. It appears in the Report Builder's visual picker for your tenant

**Tip:** Custom visuals receive the same queried dataset and theme tokens as native visuals.
`,
  },
  {
    id: 'sdk-connectors', title: 'Custom Connectors (SDK)', icon: Network,
    content: `
## Custom Connectors
Create connectors to data sources we don't support out of the box using the Connector SDK.

### Flow
1. Implement the connector interface (auth, schema discovery, fetch)
2. Register it here with its config fields
3. It becomes selectable when adding a new Data Source

**Tip:** Connectors can expose incremental fetch so large sources sync efficiently.
`,
  },

  // ── COLLABORATE ──────────────────────────────────────────────────────
  {
    id: 'discussions', title: 'Discussions', icon: MessageSquare,
    content: `
## Discussions
Threaded conversations anchored to a report, visual, data point, or started standalone — so decisions happen next to the data.

### Starting a discussion
- From a chart or Explore answer, choose **Start a thread** to attach the data context
- From the Discussions page, click **New Discussion**, give the group a name, add people, and write the first message

### Collaborating
- Type **@** to mention someone — a live list filters as you type, and they're notified by email/Slack/Teams
- Each card shows a stacked avatar row of participants
- Owners can **rename** a group; members can **leave** a thread they no longer need

**Tip:** Mentioned people are notified immediately, even if they aren't already participants.
`,
  },
  {
    id: 'notifications', title: 'Notifications', icon: Bell,
    content: `
## Notifications
Your inbox of platform events — mentions, assigned items, alert fires, schedule results, and approvals.

### Managing
- Mark read / clear, or jump straight to the source item
- Choose delivery channels (in-app, email, Slack, Teams) in Settings
- @mentions in discussions always notify you directly

**Tip:** Mute a noisy thread from its header instead of turning off all notifications.
`,
  },

  // ── ADMINISTRATION: People & Access ──────────────────────────────────
  {
    id: 'users', title: 'Users', icon: Users,
    content: `
## Users
Create, edit, deactivate, and assign roles to the people in your workspace.

### Common tasks
- **Add user** — set name, email, role, department. New users (and admin resets) must set their own password on first login
- **Change role** — controls what a user can see and do
- **Reset password** — issues a temporary password within your tenant only

**Tip:** Assignable roles are limited by your own role; only a Super Admin can grant platform-level roles.
`,
  },
  {
    id: 'sso', title: 'Enterprise SSO', icon: KeyRound,
    content: `
## Enterprise SSO
Connect your identity provider (SAML/OIDC) so users sign in with corporate credentials.

### Setup
1. Enter your IdP metadata / endpoints and certificate
2. Map IdP groups to platform roles
3. Test the connection, then enforce SSO for the workspace

**Tip:** Keep one break-glass local admin in case the IdP is unreachable.
`,
  },
  {
    id: 'audit-log', title: 'Audit Log', icon: ScrollText,
    content: `
## Audit Log
An immutable record of security-relevant actions — logins, permission and role changes, password resets, exports, and admin operations — with who, what, and when.

**Tip:** Filter by user, action, or date range to investigate an incident, then export the slice for compliance.
`,
  },
  {
    id: 'api-keys', title: 'API Keys', icon: KeyRound,
    content: `
## API Keys
Issue and revoke keys that let external systems call the platform API on your workspace's behalf.

### Good practice
- Scope each key to the minimum permissions it needs
- Name keys by integration so you know what to revoke
- Rotate keys periodically; revoke immediately if leaked

**Tip:** Keys are shown once at creation — store them securely.
`,
  },

  // ── ADMINISTRATION: AI Configuration ─────────────────────────────────
  {
    id: 'ai-agents', title: 'AI Agents', icon: Bot,
    content: `
## AI Agents
Configure the AI assistants used across the product — their model, persona, tone, and which capabilities they may use.

### What you control
- Default provider/model per agent (Explore, Coach, Briefings)
- System persona and guardrails
- Tool/capability access per agent

**Tip:** Provider/model selection here is what features like KPI Monitor's AI Scan follow.
`,
  },
  {
    id: 'voice', title: 'Voice (TTS/STT)', icon: Mic,
    content: `
## Voice
Enable spoken input (speech-to-text) and narration (text-to-speech) for Explore and briefings.

### Settings
- Choose the TTS voice and language
- Toggle voice input on supported screens
- Set narration speed

**Tip:** Voice uses your configured AI provider — check usage if you enable narration broadly.
`,
  },
  {
    id: 'ai-traces', title: 'AI Trace Log', icon: Activity,
    content: `
## AI Trace Log
A detailed log of AI calls — the prompt context, model, latency, tokens, and the grounded data used — for debugging and cost review.

**Tip:** Open a trace from a specific Explore answer to see exactly how it was produced, including which measures and filters were applied.
`,
  },
  {
    id: 'ai-usage', title: 'AI Usage & Costs', icon: DollarSign,
    content: `
## AI Usage & Costs
Track AI consumption by feature, user, and model — token counts and estimated spend — so you can manage your LLM budget.

**Tip:** If you use the Universal key, top up or enable auto top-up before you run out; this page shows how fast you're consuming it.
`,
  },

  // ── ADMINISTRATION: Workspace Settings ───────────────────────────────
  {
    id: 'branding', title: 'Branding', icon: Paintbrush,
    content: `
## Branding
Make the workspace yours — logo, colours, and theme. You can generate a full theme from a website URL, an uploaded image, a description, or hex codes.

### Tips
- Generating from a site with no usable colours returns a clear message — try Describe, Image, or hex instead
- Changes apply to the app shell, login page, and exported reports
`,
  },
  {
    id: 'version-control', title: 'Version Control', icon: GitBranch,
    content: `
## Version Control
Every change to governed assets is versioned. Browse history, compare versions, and roll back.

### What you can do
- See the timeline of changes with author and timestamp
- Diff two versions of a model, measure, or dashboard
- Restore a previous version

**Tip:** Pairs with Change Requests so approved changes are both reviewed and reversible.
`,
  },
  {
    id: 'feedback-admin', title: 'Feedback Management', icon: MessageSquare,
    content: `
## Feedback Management
Review bug reports and feedback submitted from the in-app widget, with screenshots/attachments, status, and triage.

### Workflow
- Triage incoming reports by severity
- Add internal notes and set status (open / in progress / resolved)
- Attachments under 4 MB are stored even if object storage is unavailable

**Tip:** Resolving an item can notify the reporter automatically.
`,
  },
  {
    id: 'doc-analytics', title: 'Documentation Analytics', icon: BarChart3,
    content: `
## Documentation Analytics
See how the Help Center is used — most-viewed articles, searches with no results, and gaps to fill.

**Tip:** "Searches with no results" is the best list for deciding which help article to write next.
`,
  },
  {
    id: 'pyramid-mapping', title: 'Pyramid Mapping Console', icon: Network,
    content: `
## Pyramid Mapping Console
Review how imported Pyramid model definitions map to platform semantic models — entities, fields, aggregations, and any unmapped items.

### Important
- Conversion is **deterministic** — nothing is AI-guessed
- Unknown aggregations are flagged as **warnings**, never silently converted to SUM
- Resolve warnings before relying on the imported model

**Tip:** Use the console to confirm every measure's aggregation mapped correctly after an import.
`,
  },

  // ── PLATFORM CONSOLE ─────────────────────────────────────────────────
  {
    id: 'platform-health', title: 'Platform Health', icon: Activity,
    content: `
## Platform Health
Live status of platform services — API, database, workers, queues — with uptime and recent incidents.

**Tip:** Check here first if something feels slow; a degraded worker or queue backlog usually shows up before users report it.
`,
  },
  {
    id: 'system-updates', title: 'System Updates', icon: RefreshCw,
    content: `
## System Updates
Release notes and version information for the platform, and controls to apply available updates (self-hosted deployments).

**Tip:** Review the changelog before applying an update; updates are versioned and can be rolled back.
`,
  },
  {
    id: 'site-root', title: 'Site Root & Login', icon: Globe,
    content: `
## Site Root & Login
Configure what visitors see at the site root and on the login screen — marketing landing vs direct login, and sign-up/invite behaviour.

**Tip:** Pair with Branding so the login page matches your corporate look.
`,
  },
  {
    id: 'revenue', title: 'Revenue', icon: DollarSign,
    content: `
## Revenue
Commercial overview for platform operators — subscriptions, MRR/ARR trends, and plan distribution across tenants.

**Tip:** Drill into a tenant to see its plan, seats, and usage against quota.
`,
  },
  {
    id: 'billing', title: 'Billing & Plan', icon: DollarSign,
    content: `
## Billing & Plan
Your workspace plan, seats, invoices, and usage against quota.

### Common tasks
- Review current plan and seat usage
- Download invoices
- Upgrade/downgrade or manage payment method

**Tip:** Seat counts sync automatically when you add or remove users.
`,
  },
  {
    id: 'object-storage', title: 'Object Storage', icon: HardDrive,
    content: `
## Object Storage
Configure where uploaded files — images, attachments, report assets — are stored (managed object storage or your own bucket).

### Settings
- Choose the storage backend and credentials
- Test connectivity
- Review usage

**Tip:** If object storage is unavailable, small attachments fall back to inline storage so uploads still succeed. Reach this page from the Administration Object Storage card or quick action.
`,
  },

  // ── HUBS ─────────────────────────────────────────────────────────────
  {
    id: 'administration', title: 'Administration Hub', icon: Settings,
    content: `
## Administration
The workspace admin home. Quick-action pills jump to common tasks; category cards group every admin tool: **People & Access**, **AI Configuration**, and **Workspace Settings**.

**Tip:** What you see depends on your role — tools you can't use are hidden.
`,
  },
  {
    id: 'platform-console', title: 'Platform Console', icon: Server,
    content: `
## Platform Console
The operator console for platform-level administration: **Operations**, **Customer Management**, **Commercial**, **Billing**, and **Platform Engineering** (including Object Storage, Version Control, and Logs).

**Tip:** Platform Console is for cross-tenant operators; workspace admins use the Administration hub instead.
`,
  },
];
